// @ts-nocheck
import React, { useState, useRef, useCallback, useImperativeHandle, useEffect } from 'react';
import { createPortal, flushSync } from 'react-dom';
import { useDropzone, type FileRejection } from 'react-dropzone';
import { useLocalization, type Language } from '../../config/localization';
import Icon, { ICONS } from '../ui/icons';
import type { IWorkflowWidget, WidgetPayloadResult, WidgetValidationError, GalleryMediaItem } from '../../interfaces/IWorkflowWidget';
import GallerySelectionModal from '../ui/overlays/modals/GallerySelectionModal';
import CropAdjustmentModal from '../ui/modals/CropAdjustmentModal';
import type { GalleryImage } from '../pages/gallery/GalleryContent';
import { getMediaTypeFromUrl } from '../../utils/mediaTypeDetection';
import { processImageForUpload, cropAndResizeToTarget, convertToJpeg as convertFileToJpeg, type CropAndResizeResult } from '../../utils/imageCompressor';
import BaseWidget from './BaseWidget';
import { readRecreateBlobForOrg } from '../../utils/recreateStorage';
import { getInvalidPricingAggregateMessage } from '../../config/pricingAggregateMessages';
import { getUnmeasuredMediaPricingKeys, sumExactMediaDurations } from '../../utils/pricingAggregateAssign';
import {
  describeVideoSlotConstraints,
  validateVideoSlotConstraints,
  type VideoSlotConstraints,
} from '../../utils/slotVideoConstraints';
import { probeVideoMetadata } from '../../utils/videoFrameStats';

// Media types
type MediaType = 'image' | 'video' | 'audio';

// Slot configuration interface (same as big widget)
export interface SlotConfig extends VideoSlotConstraints {
  slot_id: string;
  nameRu: string;
  nameEn: string;
  mediaType: MediaType;
  required: boolean;
  maxCount: number;
  // Accepted file formats (e.g. ['.mp4']). Empty/undefined = all defaults for the media type.
  acceptedFormats?: string[];
  imageMaxFileSize?: number;
  imageMinWidth?: number;
  imageMaxWidth?: number;
  imageMinHeight?: number;
  imageMaxHeight?: number;
  audioMaxFileSize?: number;
  audioMaxDuration?: number;
  // Target dimensions (center crop + resize)
  targetWidth?: number;
  targetHeight?: number;
  // JPEG conversion (flatten alpha to white background)
  convertToJpeg?: boolean;
}

const DEFAULT_FORMATS: Record<MediaType, string[]> = {
  image: ['.png', '.jpg', '.jpeg', '.gif', '.webp'],
  video: ['.mp4', '.webm', '.mov', '.avi'],
  audio: ['.mp3', '.wav', '.ogg', '.flac', '.m4a'],
};

const EXT_TO_MIME: Record<string, string> = {
  '.png': 'image/png', '.jpg': 'image/jpeg', '.jpeg': 'image/jpeg',
  '.gif': 'image/gif', '.webp': 'image/webp',
  '.mp4': 'video/mp4', '.webm': 'video/webm', '.mov': 'video/quicktime', '.avi': 'video/x-msvideo',
  '.mp3': 'audio/mpeg', '.wav': 'audio/wav', '.ogg': 'audio/ogg', '.flac': 'audio/flac', '.m4a': 'audio/mp4',
};

export interface SlotsInputMediaConfig {
  slots: SlotConfig[];
}

// Media item stored in a slot
interface SlotMediaItem {
  file?: File;
  url: string;
  mediaType: MediaType;
  dimensions?: { width: number; height: number };
  duration?: number;
  fps?: number;
  frameCount?: number;
  sizeBytes?: number;
  videoStatsProbeId?: string;
  source: 'file' | 'gallery';
  galleryId?: number;
  gallerySelectionToken?: string;
  processing?: boolean;
  originalFile?: File;
  originalUrl?: string;
  originalDimensions?: { width: number; height: number };
  wasCropped?: boolean;
}

// State for each slot
interface SlotState {
  slotId: string;
  items: SlotMediaItem[];
}

interface SlotsInputMediaSmallWidgetProps {
  language: Language;
  config: SlotsInputMediaConfig;
  className?: string;
  widgetRef?: React.RefObject<IWorkflowWidget>;
  /**
   * Active organization. Required to accept a `recreate-slots-data` /
   * `recreate-media-urls` hand-off: those blobs carry one tenant's gallery URLs
   * and are fed into a BILLED generation, so an absent/mismatched id drops
   * them. See `src/utils/recreateStorage.ts`.
   */
  organizationId?: number | null;
}

// Format file size
function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  return `${Math.round(bytes / 1024)} KB`;
}

// Get media type from file
function getMediaType(file: File): MediaType {
  if (file.type.startsWith('image/')) return 'image';
  if (file.type.startsWith('video/')) return 'video';
  if (file.type.startsWith('audio/')) return 'audio';
  return 'image';
}

// Auto-dismiss delay for processed-file notifications (ms)
const NOTIFICATION_DISMISS_MS = 5000;

// Format duration
function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export default function SlotsInputMediaSmallWidget({
  language,
  config: providedConfig,
  className = '',
  widgetRef,
  organizationId
}: SlotsInputMediaSmallWidgetProps) {
  const t = useLocalization(language);
  const unsupportedModelSlots = React.useMemo(
    () => (providedConfig?.slots ?? []).filter(slot => slot.mediaType === 'model'),
    [providedConfig]
  );
  // Model slots are intentionally Big-widget-only. Keep them out of media processing, but retain
  // a blocking validation/UI signal so a stale required slot cannot silently disappear.
  const config = React.useMemo(() => ({
    ...providedConfig,
    slots: (providedConfig?.slots ?? []).filter(slot => slot.mediaType !== 'model'),
  }), [providedConfig]);
  const [slotsState, setSlotsState] = useState<SlotState[]>([]);
  const [activeGallerySlotId, setActiveGallerySlotId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [processedNotifications, setProcessedNotifications] = useState<string[]>([]);
  const [cropModalSlotId, setCropModalSlotId] = useState<string | null>(null);
  const [cropModalItemIndex, setCropModalItemIndex] = useState<number | null>(null);
  const notificationTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const mediaUrlMapRef = useRef<Map<File, string>>(new Map());
  const videoStatsProbeSequenceRef = useRef(0);
  const videoStatsProbeControllersRef = useRef<Map<string, AbortController>>(new Map());
  const completedVideoStatsProbesRef = useRef<Set<string>>(new Set());
  const slotsStateRef = useRef(slotsState);
  slotsStateRef.current = slotsState;

  const nextVideoStatsProbeId = useCallback(() =>
    `small-slots-video-${++videoStatsProbeSequenceRef.current}`, []);

  const cancelVideoStatsProbe = useCallback((probeId?: string) => {
    if (!probeId) return;
    videoStatsProbeControllersRef.current.get(probeId)?.abort();
    videoStatsProbeControllersRef.current.delete(probeId);
    completedVideoStatsProbesRef.current.delete(probeId);
  }, []);

  const cancelAllVideoStatsProbes = useCallback(() => {
    videoStatsProbeControllersRef.current.forEach(controller => controller.abort());
    videoStatsProbeControllersRef.current.clear();
    completedVideoStatsProbesRef.current.clear();
  }, []);

  // Centralized notification helper — clears previous timeout, shows messages, auto-dismisses
  const showNotifications = useCallback((messages: string[]) => {
    if (notificationTimeoutRef.current) {
      clearTimeout(notificationTimeoutRef.current);
    }
    setProcessedNotifications(messages);
    notificationTimeoutRef.current = setTimeout(() => {
      setProcessedNotifications([]);
    }, NOTIFICATION_DISMISS_MS);
  }, []);

  // Initialize slots state from config — depend on slot IDs and constraints,
  // so switching workflows or changing config resets state properly.
  const slotIds = JSON.stringify(config?.slots ?? []);
  // Empties every slot and releases the blob URLs its items hold.
  const resetSlotsState = useCallback(() => {
    if (!config?.slots) return;
    cancelAllVideoStatsProbes();
    // Revoke all existing blob URLs before resetting slots
    mediaUrlMapRef.current.forEach(url => URL.revokeObjectURL(url));
    mediaUrlMapRef.current.clear();
    // Also revoke originalUrl blob URLs stored on cropped items
    slotsStateRef.current.forEach(slot => {
      slot.items.forEach(item => {
        if (item.originalUrl) URL.revokeObjectURL(item.originalUrl);
      });
    });
    // Close crop modal if open — slot items are being reset
    setCropModalSlotId(null);
    setCropModalItemIndex(null);
    setSlotsState(
      config.slots.map(slot => ({
        slotId: slot.slot_id,
        items: []
      }))
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slotIds]);

  useEffect(() => {
    resetSlotsState();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slotIds]);

  // Drop staged media when the tenant changes.
  //
  // Slot items are the input media of a BILLED generation and come from the
  // CURRENT organization's gallery — the picker, or the recreate hand-off
  // below, both of which keep that organization's own R2 URLs. Carrying them
  // across a switch files org A's assets into org B's generation and history.
  // Locally-dropped files go too: once staged they are indistinguishable, and
  // re-attaching costs a drop. Ref-guarded rather than a bare dep compare
  // because `currentOrganization` blips through null while the org context
  // refreshes — `5 → null → 5` is not a switch.
  const stagedOrgRef = useRef<number | null>(null);
  useEffect(() => {
    if (!organizationId) return;
    if (stagedOrgRef.current !== null && stagedOrgRef.current !== organizationId) {
      resetSlotsState();
    }
    stagedOrgRef.current = organizationId;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  // Handle recreate media from sessionStorage (per-slot data preferred, flat array fallback)
  useEffect(() => {
    if (!config?.slots?.length) return;
    // Wait for the organization to resolve before touching the blobs: reading
    // them with an unknown tenant would reject AND delete a legitimate same-org
    // hand-off (a cold reload — F5 or the `lazyWithRetry` post-deploy reload —
    // can mount this widget before the org lands).
    if (!organizationId) return;

    // Try per-slot data first (from recreate-slots-data)
    const slotsDataJson = sessionStorage.getItem('recreate-slots-data');
    if (slotsDataJson) {
      sessionStorage.removeItem('recreate-slots-data');

      try {
        // Fails closed on a foreign/unstamped blob: these URLs become the input
        // media of a billed generation. Note the 30s freshness check below does
        // NOT bound that — a stale blob falls through to the flat array, which
        // is why both keys carry the stamp. See `src/utils/recreateStorage.ts`.
        const slotsData = readRecreateBlobForOrg(slotsDataJson, organizationId) as
          { slotMappings?: { slotId: string; urls: string[] }[]; timestamp?: number } | null;
        if (slotsData?.slotMappings && Date.now() - (slotsData.timestamp || 0) <= 30000) {
          const mappings: { slotId: string; urls: string[] }[] = slotsData.slotMappings;

          // Check if ALL stored mappings (with URLs) have a matching slot in current workflow.
          // If any stored slot_id is missing (renamed/removed), fall back to flat array
          // to avoid silently losing media for unmatched slots.
          const mappingsWithUrls = mappings.filter(m => m.urls.length > 0);
          const allMappingsHaveSlots = mappingsWithUrls.length > 0 && mappingsWithUrls.every(m =>
            config.slots.some(slot => slot.slot_id === m.slotId)
          );

          if (allMappingsHaveSlots) {
            sessionStorage.removeItem('recreate-media-urls'); // Clean up fallback key only when matches found
            const newSlotsState: SlotState[] = config.slots.map(slot => {
              const mapping = mappings.find(m => m.slotId === slot.slot_id);
              if (mapping && mapping.urls.length > 0) {
                return {
                  slotId: slot.slot_id,
                  items: mapping.urls.slice(0, slot.maxCount).map(url => ({
                    url,
                    mediaType: slot.mediaType,
                    source: 'gallery' as const,
                    galleryId: -1,
                    ...(slot.mediaType === 'video'
                      ? { videoStatsProbeId: nextVideoStatsProbeId() }
                      : {}),
                  }))
                };
              }
              return { slotId: slot.slot_id, items: [] };
            });

            setSlotsState(newSlotsState);
            return;
          }
          // No slot IDs matched — fall through to flat array fallback
        }
      } catch (error) {
        console.error('Failed to load recreate per-slot data:', error);
      }
    }

    // Fallback: flat array from recreate-media-urls
    const mediaUrlsJson = sessionStorage.getItem('recreate-media-urls');
    if (mediaUrlsJson) {
      sessionStorage.removeItem('recreate-media-urls');

      try {
        // Same fail-closed stamp check as the per-slot blob above.
        const mediaUrls = readRecreateBlobForOrg(mediaUrlsJson, organizationId)?.mediaUrls;

        if (Array.isArray(mediaUrls) && mediaUrls.length > 0) {
          const newSlotsState: SlotState[] = config.slots.map((slot, index) => {
            if (config.slots.length === 1) {
              return {
                slotId: slot.slot_id,
                items: mediaUrls.slice(0, slot.maxCount).map(url => ({
                  url,
                  mediaType: slot.mediaType,
                  source: 'gallery' as const,
                  galleryId: -1,
                  ...(slot.mediaType === 'video'
                    ? { videoStatsProbeId: nextVideoStatsProbeId() }
                    : {}),
                }))
              };
            } else {
              const url = mediaUrls[index];
              if (url) {
                return {
                  slotId: slot.slot_id,
                  items: [{
                    url,
                    mediaType: slot.mediaType,
                    source: 'gallery' as const,
                    galleryId: -1,
                    ...(slot.mediaType === 'video'
                      ? { videoStatsProbeId: nextVideoStatsProbeId() }
                      : {}),
                  }]
                };
              }
            }
            return { slotId: slot.slot_id, items: [] };
          });

          setSlotsState(newSlotsState);
        }
      } catch (error) {
        console.error('Failed to load recreate media URLs:', error);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slotIds, organizationId]);

  // Get metadata from URL for gallery items (mirrors BigSlotsInputMediaWidget)
  const getMediaMetadataFromUrl = useCallback((url: string, mediaType: MediaType): Promise<{
    dimensions?: { width: number; height: number };
    duration?: number;
  }> => {
    return new Promise((resolve) => {
      if (mediaType === 'image') {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          resolve({ dimensions: { width: img.naturalWidth, height: img.naturalHeight } });
        };
        img.onerror = () => resolve({});
        img.src = url;
      } else if (mediaType === 'video') {
        const video = document.createElement('video');
        video.onloadedmetadata = () => {
          resolve({
            dimensions: { width: video.videoWidth, height: video.videoHeight },
            duration: video.duration
          });
        };
        video.onerror = () => resolve({});
        video.src = url;
      } else if (mediaType === 'audio') {
        const audio = document.createElement('audio');
        audio.onloadedmetadata = () => {
          resolve({ duration: audio.duration });
        };
        audio.onerror = () => resolve({});
        audio.src = url;
      } else {
        resolve({});
      }
    });
  }, []);

  // Auto-load metadata for gallery items missing dimensions/duration (handles recreate and fallback cases).
  // handleGallerySelect already loads metadata for interactive gallery picks; this covers recreate URLs.
  useEffect(() => {
    const loadMissingMetadata = async () => {
      // Collect gallery items that need metadata (no dimensions for images, no duration for audio/video)
      // Capture URL to guard against race conditions — only apply metadata if URL still matches
      const itemsToLoad: { slotId: string; itemIndex: number; url: string; mediaType: MediaType }[] = [];

      slotsState.forEach(slot => {
        slot.items.forEach((item, index) => {
          if (item.source !== 'gallery') return;
          const needsMetadata =
            (item.mediaType === 'image' && !item.dimensions) ||
            ((item.mediaType === 'audio' || item.mediaType === 'video') && item.duration === undefined);
          if (needsMetadata) {
            itemsToLoad.push({ slotId: slot.slotId, itemIndex: index, url: item.url, mediaType: item.mediaType });
          }
        });
      });

      if (itemsToLoad.length === 0) return;

      // Load metadata for all items using the unified helper
      const results = await Promise.all(
        itemsToLoad.map(async (item) => {
          try {
            const metadata = await getMediaMetadataFromUrl(item.url, item.mediaType);
            return { slotId: item.slotId, itemIndex: item.itemIndex, metadata };
          } catch {
            return { slotId: item.slotId, itemIndex: item.itemIndex, metadata: {} };
          }
        })
      );

      // Build an update map keyed by slotId → itemIndex → metadata (with URL for race-condition guard)
      const updatesMap = new Map<string, Map<number, { url: string; dimensions?: { width: number; height: number }; duration?: number }>>();
      results.forEach(({ slotId, itemIndex, metadata }) => {
        if (metadata.dimensions || metadata.duration !== undefined) {
          if (!updatesMap.has(slotId)) updatesMap.set(slotId, new Map());
          const originalUrl = itemsToLoad.find(i => i.slotId === slotId && i.itemIndex === itemIndex)?.url ?? '';
          updatesMap.get(slotId)!.set(itemIndex, { ...metadata, url: originalUrl });
        }
      });

      if (updatesMap.size > 0) {
        setSlotsState(prev => prev.map(slot => {
          const slotUpdates = updatesMap.get(slot.slotId);
          if (!slotUpdates) return slot;
          return {
            ...slot,
            items: slot.items.map((item, index) => {
              const update = slotUpdates.get(index);
              // Guard: only apply if item URL still matches (prevents stale async race)
              if (!update || item.url !== update.url) return item;
              return {
                ...item,
                ...(update.dimensions ? { dimensions: update.dimensions } : {}),
                ...(update.duration !== undefined ? { duration: update.duration } : {})
              };
            })
          };
        }));
      }
    };

    loadMissingMetadata();
  }, [slotsState, getMediaMetadataFromUrl]);

  // Frame stats are supplementary and fail-open. Probe each video identity once,
  // then merge only positive measurements without affecting duration validation.
  useEffect(() => {
    const activeProbeIds = new Set<string>();

    slotsState.forEach(slot => {
      slot.items.forEach(item => {
        if (item.mediaType !== 'video' || !item.videoStatsProbeId) return;
        const probeId = item.videoStatsProbeId;
        activeProbeIds.add(probeId);

        if (
          completedVideoStatsProbesRef.current.has(probeId)
          || videoStatsProbeControllersRef.current.has(probeId)
        ) return;

        const controller = new AbortController();
        videoStatsProbeControllersRef.current.set(probeId, controller);
        const file = item.source === 'file' ? item.file : undefined;

        probeVideoMetadata(
          { file, url: item.url, duration: item.duration },
          controller.signal,
        ).then(metadata => {
          if (controller.signal.aborted) return;
          completedVideoStatsProbesRef.current.add(probeId);
          videoStatsProbeControllersRef.current.delete(probeId);
          if (!metadata) return;

          setSlotsState(prev => prev.map(currentSlot => ({
            ...currentSlot,
            items: currentSlot.items.map(currentItem => {
              if (currentItem.videoStatsProbeId !== probeId) return currentItem;
              return {
                ...currentItem,
                ...metadata,
              };
            }),
          })));
        }).catch(() => {
          if (controller.signal.aborted) return;
          controller.abort();
          completedVideoStatsProbesRef.current.add(probeId);
          videoStatsProbeControllersRef.current.delete(probeId);
        });
      });
    });

    for (const probeId of videoStatsProbeControllersRef.current.keys()) {
      if (!activeProbeIds.has(probeId)) cancelVideoStatsProbe(probeId);
    }
  }, [slotsState, cancelVideoStatsProbe]);

  // Cleanup URLs and timers on unmount
  useEffect(() => {
    return () => {
      cancelAllVideoStatsProbes();
      mediaUrlMapRef.current.forEach(url => URL.revokeObjectURL(url));
      mediaUrlMapRef.current.clear();
      // Revoke originalUrl blob URLs stored on cropped items (ref avoids stale closure)
      slotsStateRef.current.forEach(slot => {
        slot.items.forEach(item => {
          if (item.originalUrl) URL.revokeObjectURL(item.originalUrl);
        });
      });
      if (notificationTimeoutRef.current) {
        clearTimeout(notificationTimeoutRef.current);
      }
    };
  }, [cancelAllVideoStatsProbes]);

  // Dispatch selection change event for cost recalculation (matches BigInputMediaWidget pattern)
  useEffect(() => {
    const totalItems = slotsState.reduce((sum, s) => sum + s.items.length, 0);
    window.dispatchEvent(new CustomEvent('inputMediaSelectionChange', {
      detail: { selectedCount: totalItems, totalCount: totalItems }
    }));
  }, [slotsState]);

  // Get slot name based on language
  const getSlotName = useCallback((slot: SlotConfig): string => {
    return language === 'ru' ? slot.nameRu : slot.nameEn;
  }, [language]);

  // Get slot config by ID
  const getSlotConfig = useCallback((slotId: string): SlotConfig | undefined => {
    return config?.slots?.find(s => s.slot_id === slotId);
  }, [config]);

  // Get slot state by ID
  const getSlotState = useCallback((slotId: string): SlotState | undefined => {
    return slotsState.find(s => s.slotId === slotId);
  }, [slotsState]);

  // Get media metadata (duration for video/audio)
  const getMediaMetadata = useCallback((file: File, mediaType: 'video' | 'audio'): Promise<{
    duration: number;
    dimensions?: { width: number; height: number };
  }> => {
    return new Promise((resolve, reject) => {
      const element = mediaType === 'video'
        ? document.createElement('video')
        : document.createElement('audio');
      const url = URL.createObjectURL(file);

      element.onloadedmetadata = () => {
        URL.revokeObjectURL(url);
        const result: { duration: number; dimensions?: { width: number; height: number } } = {
          duration: element.duration
        };
        if (mediaType === 'video' && element instanceof HTMLVideoElement) {
          result.dimensions = {
            width: element.videoWidth,
            height: element.videoHeight
          };
        }
        resolve(result);
      };

      element.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to load media'));
      };

      element.src = url;
    });
  }, []);

  // Validate file against slot restrictions
  const validateFile = useCallback(async (
    file: File,
    slotConfig: SlotConfig
  ): Promise<{ valid: boolean; error?: string; dimensions?: { width: number; height: number }; duration?: number; processedFile?: File; processedNotification?: string; originalFile?: File; originalDimensions?: { width: number; height: number }; wasCropped?: boolean }> => {
    const mediaType = getMediaType(file);

    // Check media type matches slot
    if (mediaType !== slotConfig.mediaType) {
      return {
        valid: false,
        error: t.widgets?.slotsInputMedia?.errors?.wrongType || 'Wrong file type'
      };
    }

    // Check file extension against accepted formats
    if (slotConfig.acceptedFormats?.length) {
      const ext = '.' + (file.name.toLowerCase().split('.').pop() || '');
      if (!slotConfig.acceptedFormats.includes(ext)) {
        const unsupported = t.widgets?.slotsInputMedia?.unsupportedFormat || 'Unsupported format';
        const allowed = t.widgets?.slotsInputMedia?.allowedFormats || 'Allowed formats';
        return { valid: false, error: `${unsupported}: ${ext}. ${allowed}: ${slotConfig.acceptedFormats.join(', ')}` };
      }
    }

    // Validate image
    if (mediaType === 'image') {
      const maxSizeBytes = (slotConfig.imageMaxFileSize || 20) * 1024 * 1024;

      try {
        // Use processImageForUpload to auto-resize/compress instead of rejecting
        const processResult = await processImageForUpload(file, {
          maxWidth: slotConfig.imageMaxWidth ?? undefined,
          maxHeight: slotConfig.imageMaxHeight ?? undefined,
          maxFileSizeBytes: maxSizeBytes
        });

        let currentFile = processResult.file;
        let dims = processResult.finalDimensions || { width: 0, height: 0 };

        // Step 2: Crop/resize to target dimensions if configured
        // Save pre-crop state so user can re-crop later via CropAdjustmentModal
        const preCropFile = currentFile;
        const preCropDims = { ...dims };
        let cropResult: CropAndResizeResult | undefined;
        if (slotConfig.targetWidth && slotConfig.targetHeight) {
          const result = await cropAndResizeToTarget(currentFile, {
            targetWidth: slotConfig.targetWidth,
            targetHeight: slotConfig.targetHeight,
            convertToJpeg: slotConfig.convertToJpeg
          });
          cropResult = result;
          currentFile = result.file;
          dims = result.finalDimensions;
        } else if (slotConfig.convertToJpeg) {
          // JPEG-only conversion (no crop)
          const result = await convertFileToJpeg(currentFile);
          cropResult = result;
          currentFile = result.file;
          dims = result.finalDimensions;
        }

        // Check min dimensions on the final result (still reject if too small)
        if (slotConfig.imageMinWidth && dims.width < slotConfig.imageMinWidth) {
          return { valid: false, error: `Width ${dims.width}px < min ${slotConfig.imageMinWidth}px` };
        }
        if (slotConfig.imageMinHeight && dims.height < slotConfig.imageMinHeight) {
          return { valid: false, error: `Height ${dims.height}px < min ${slotConfig.imageMinHeight}px` };
        }

        // If processed file still exceeds size limit, reject
        if (currentFile.size > maxSizeBytes) {
          return { valid: false, error: `${t.widgets?.inputMedia?.fileTooLarge || 'File too large'} (max ${slotConfig.imageMaxFileSize || 20}MB)` };
        }

        // Build notification if auto-processed
        const notificationParts: string[] = [];
        if (processResult.wasProcessed) {
          const actions = processResult.actions;
          if (actions.includes('resized') && actions.includes('compressed')) {
            notificationParts.push(t.widgets?.inputMedia?.imageAutoResizedAndCompressed || 'Image resized and compressed');
          } else if (actions.includes('resized')) {
            notificationParts.push(t.widgets?.inputMedia?.imageAutoResized || 'Image resized to fit allowed dimensions');
          } else {
            notificationParts.push(t.widgets?.inputMedia?.imageAutoCompressed || 'Image compressed to fit size limit');
          }
        }
        if (cropResult?.wasProcessed) {
          const cropActions = cropResult.actions;
          const hasCrop = cropActions.includes('cropped');
          const hasResize = cropActions.includes('resized');
          const hasJpeg = cropActions.includes('jpeg-converted');
          const dimStr = `(${dims.width}x${dims.height})`;
          if (hasCrop && hasJpeg) {
            notificationParts.push(
              `${t.widgets?.inputMedia?.imageCroppedAndConverted || 'Cropped and converted to JPEG'} ${dimStr}`
            );
          } else if (hasResize && hasJpeg) {
            notificationParts.push(
              `${t.widgets?.inputMedia?.imageResizedAndConverted || 'Resized and converted to JPEG'} ${dimStr}`
            );
          } else if (hasCrop) {
            notificationParts.push(
              `${t.widgets?.inputMedia?.imageCroppedToTarget || 'Cropped to'} ${dimStr}`
            );
          } else if (hasResize) {
            notificationParts.push(
              `${t.widgets?.inputMedia?.imageResizedToTarget || 'Resized to'} ${dimStr}`
            );
          } else if (hasJpeg) {
            notificationParts.push(t.widgets?.inputMedia?.imageConvertedToJpeg || 'Converted to JPEG');
          }
        }

        const wasProcessed = processResult.wasProcessed || (cropResult?.wasProcessed ?? false);
        const wasCropped = cropResult?.actions.includes('cropped') ?? false;
        const processedNotification = notificationParts.length > 0
          ? `${file.name}: ${notificationParts.join(', ')}`
          : undefined;

        return {
          valid: true,
          dimensions: dims,
          processedFile: wasProcessed ? currentFile : undefined,
          processedNotification,
          ...(wasCropped ? {
            originalFile: preCropFile,
            originalDimensions: preCropDims,
            wasCropped: true
          } : {})
        };
      } catch {
        return { valid: false, error: t.widgets?.inputMedia?.invalidFile || 'Failed to load image' };
      }
    }

    // Validate video
    if (mediaType === 'video') {
      const maxSize = (slotConfig.videoMaxFileSize || 100) * 1024 * 1024;
      if (file.size > maxSize) {
        return {
          valid: false,
          error: `${t.widgets?.slotsInputMedia?.errors?.fileTooLarge || 'File too large'} (max ${slotConfig.videoMaxFileSize}MB)`
        };
      }

      try {
        const metadata = await getMediaMetadata(file, 'video');
        const constraintError = validateVideoSlotConstraints({
          width: metadata.dimensions?.width,
          height: metadata.dimensions?.height,
          duration: metadata.duration,
        }, slotConfig, t.widgets?.slotsInputMedia?.errors);
        if (constraintError) {
          return { valid: false, error: constraintError };
        }
        return { valid: true, duration: metadata.duration, dimensions: metadata.dimensions };
      } catch {
        return { valid: true }; // Allow if can't read metadata
      }
    }

    // Validate audio
    if (mediaType === 'audio') {
      const maxSize = (slotConfig.audioMaxFileSize || 50) * 1024 * 1024;
      if (file.size > maxSize) {
        return {
          valid: false,
          error: `${t.widgets?.slotsInputMedia?.errors?.fileTooLarge || 'File too large'} (max ${slotConfig.audioMaxFileSize}MB)`
        };
      }

      try {
        const metadata = await getMediaMetadata(file, 'audio');
        if (slotConfig.audioMaxDuration && metadata.duration > slotConfig.audioMaxDuration) {
          return {
            valid: false,
            error: `Audio must be at most ${slotConfig.audioMaxDuration} seconds`
          };
        }
        return { valid: true, duration: metadata.duration };
      } catch {
        return { valid: true }; // Allow if can't read metadata
      }
    }

    return { valid: true };
  }, [t, getMediaMetadata]);

  // Add media to slot
  const addMediaToSlot = useCallback(async (slotId: string, files: File[]) => {
    const slotConfig = getSlotConfig(slotId);
    if (!slotConfig) return;

    setErrorMessage(null);
    const notifications: string[] = [];

    const currentItems = getSlotState(slotId)?.items || [];
    const isReplaceMode = slotConfig.maxCount === 1 && currentItems.length >= 1;

    if (isReplaceMode) {
      // Replace existing file with first new file
      const file = files[0];
      if (!file) return;

      const validation = await validateFile(file, slotConfig);
      if (!validation.valid) {
        setErrorMessage(validation.error || 'Invalid file');
        setTimeout(() => setErrorMessage(null), 3000);
        return;
      }

      // Revoke old blob URL if item has a local file (covers both source: 'file' and processed gallery items)
      const oldItem = currentItems[0];
      cancelVideoStatsProbe(oldItem?.videoStatsProbeId);
      if (oldItem?.file) {
        const oldUrl = mediaUrlMapRef.current.get(oldItem.file);
        if (oldUrl) {
          URL.revokeObjectURL(oldUrl);
          mediaUrlMapRef.current.delete(oldItem.file);
        }
      }
      if (oldItem?.originalUrl) {
        URL.revokeObjectURL(oldItem.originalUrl);
      }

      const fileToUse = validation.processedFile || file;
      const url = URL.createObjectURL(fileToUse);
      mediaUrlMapRef.current.set(fileToUse, url);

      const newItem: SlotMediaItem = {
        file: fileToUse,
        url,
        mediaType: slotConfig.mediaType,
        dimensions: validation.dimensions,
        duration: validation.duration,
        source: 'file',
        ...(slotConfig.mediaType === 'video'
          ? { sizeBytes: fileToUse.size, videoStatsProbeId: nextVideoStatsProbeId() }
          : {}),
        ...(validation.wasCropped ? {
          originalFile: validation.originalFile,
          originalUrl: URL.createObjectURL(validation.originalFile!),
          originalDimensions: validation.originalDimensions,
          wasCropped: true
        } : {})
      };

      setSlotsState(prev => prev.map(s =>
        s.slotId === slotId ? { ...s, items: [newItem] } : s
      ));

      if (validation.processedNotification) {
        showNotifications([validation.processedNotification]);
      }
      return;
    }

    const remaining = Math.max(0, slotConfig.maxCount - currentItems.length);
    const filesToProcess = files.slice(0, remaining);

    for (const file of filesToProcess) {
      const validation = await validateFile(file, slotConfig);
      if (!validation.valid) {
        setErrorMessage(validation.error || 'Invalid file');
        setTimeout(() => setErrorMessage(null), 3000);
        continue;
      }

      // Use processed file if auto-conversion happened, otherwise original
      const fileToUse = validation.processedFile || file;

      const url = URL.createObjectURL(fileToUse);
      mediaUrlMapRef.current.set(fileToUse, url);

      const newItem: SlotMediaItem = {
        file: fileToUse,
        url,
        mediaType: slotConfig.mediaType,
        dimensions: validation.dimensions,
        duration: validation.duration,
        source: 'file',
        ...(slotConfig.mediaType === 'video'
          ? { sizeBytes: fileToUse.size, videoStatsProbeId: nextVideoStatsProbeId() }
          : {}),
        ...(validation.wasCropped ? {
          originalFile: validation.originalFile,
          originalUrl: URL.createObjectURL(validation.originalFile!),
          originalDimensions: validation.originalDimensions,
          wasCropped: true
        } : {})
      };

      setSlotsState(prev => prev.map(s =>
        s.slotId === slotId
          ? { ...s, items: [...s.items, newItem].slice(0, slotConfig.maxCount) }
          : s
      ));

      if (validation.processedNotification) {
        notifications.push(validation.processedNotification);
      }
    }

    // Show auto-processing notifications with auto-dismiss
    if (notifications.length > 0) {
      showNotifications(notifications);
    }
  }, [getSlotConfig, getSlotState, validateFile, showNotifications, cancelVideoStatsProbe, nextVideoStatsProbeId]);

  // Remove media from slot
  const removeMediaFromSlot = useCallback((slotId: string, itemIndex: number) => {
    setSlotsState(prev => prev.map(s => {
      if (s.slotId !== slotId) return s;
      const item = s.items[itemIndex];
      cancelVideoStatsProbe(item?.videoStatsProbeId);
      if (item?.file) {
        const url = mediaUrlMapRef.current.get(item.file);
        if (url) {
          URL.revokeObjectURL(url);
          mediaUrlMapRef.current.delete(item.file);
        }
      }
      if (item?.originalUrl) {
        URL.revokeObjectURL(item.originalUrl);
      }
      return { ...s, items: s.items.filter((_, i) => i !== itemIndex) };
    }));
  }, [cancelVideoStatsProbe]);

  // Handle gallery selection for a slot
  const gallerySelectionSequenceRef = useRef(0);

  const handleGallerySelect = useCallback(async (selectedImages: GalleryImage[]) => {
    if (!activeGallerySlotId) return;

    const slotConfig = getSlotConfig(activeGallerySlotId);
    if (!slotConfig) return;

    const parseUrls = (urlsJson?: string): string[] => {
      if (!urlsJson) return [];
      try {
        const parsed = JSON.parse(urlsJson);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    };

    const newItems: SlotMediaItem[] = [];
    for (const img of selectedImages) {
      let url = '';
      const originalUrls = parseUrls(img.original_urls);
      const previewUrls = parseUrls(img.preview_urls);
      const externalUrls = parseUrls(img.external_urls);

      if (originalUrls.length > 0) url = originalUrls[0];
      else if (previewUrls.length > 0) url = previewUrls[0];
      else if (externalUrls.length > 0) url = externalUrls[0];

      // Determine media type
      // Note: DB stores simple types ('audio', 'video', 'image'), not MIME types
      let mediaType: MediaType = 'image';
      if (img.contentType?.startsWith('video')) {
        mediaType = 'video';
      } else if (img.contentType?.startsWith('audio')) {
        mediaType = 'audio';
      } else if (url) {
        mediaType = getMediaTypeFromUrl(url);
      }

      // Only add if matches slot type
      if (mediaType !== slotConfig.mediaType) continue;

      // Check format if restricted
      if (slotConfig.acceptedFormats?.length && url) {
        const urlPath = url.split('?')[0].toLowerCase();
        const urlExt = '.' + (urlPath.split('.').pop() || '');
        if (!slotConfig.acceptedFormats.includes(urlExt)) continue;
      }

      newItems.push({
        url,
        mediaType,
        source: 'gallery',
        galleryId: img.id,
        gallerySelectionToken: `${activeGallerySlotId}:${++gallerySelectionSequenceRef.current}`,
        ...(mediaType === 'video'
          ? { videoStatsProbeId: nextVideoStatsProbeId() }
          : {}),
      });

      if (newItems.length >= slotConfig.maxCount) break;
    }

    // Determine early if processing will be needed so we can set the flag immediately
    const needsProcessing = slotConfig.mediaType === 'image' &&
      ((slotConfig.targetWidth && slotConfig.targetHeight) || slotConfig.convertToJpeg);

    // If processing is needed, mark image items with processing: true from the start
    // to prevent submission during the metadata loading + processing window
    if (needsProcessing) {
      newItems.forEach((item, i) => {
        if (item.mediaType === 'image') {
          newItems[i] = { ...item, processing: true };
        }
      });
    }

    // Revoke old blob URLs before replacing with gallery items
    const oldSlotItems = slotsState.find(s => s.slotId === activeGallerySlotId)?.items || [];
    oldSlotItems.forEach(item => {
      cancelVideoStatsProbe(item.videoStatsProbeId);
      if (item.file) {
        const url = mediaUrlMapRef.current.get(item.file);
        if (url) {
          URL.revokeObjectURL(url);
          mediaUrlMapRef.current.delete(item.file);
        }
      }
      if (item.originalUrl) {
        URL.revokeObjectURL(item.originalUrl);
      }
    });

    // Replace slot items with gallery selection (with processing flag already set if needed)
    setSlotsState(prev => prev.map(s =>
      s.slotId === activeGallerySlotId
        ? { ...s, items: newItems }
        : s
    ));

    const slotIdForMetadata = activeGallerySlotId;
    setActiveGallerySlotId(null);

    // Load metadata for all media types (images: dimensions; audio/video: duration)
    const metadataPromises = newItems.map(async (item) => {
      try {
        return {
          token: item.gallerySelectionToken,
          metadata: await getMediaMetadataFromUrl(item.url, item.mediaType),
        };
      } catch {
        return { token: item.gallerySelectionToken, metadata: {} };
      }
    });

    const results = await Promise.all(metadataPromises);

    // Validate video duration and resolution constraints for gallery items
    const rejectedTokens = new Set<string>();
    if (slotConfig.mediaType === 'video') {
      results.forEach(({ token, metadata }) => {
        const constraintError = validateVideoSlotConstraints({
          width: metadata.dimensions?.width,
          height: metadata.dimensions?.height,
          duration: metadata.duration,
        }, slotConfig, t.widgets?.slotsInputMedia?.errors);
        if (constraintError) {
          setErrorMessage(constraintError);
          if (token) rejectedTokens.add(token);
        }
      });
      if (rejectedTokens.size > 0) {
        newItems.forEach(item => {
          if (item.gallerySelectionToken && rejectedTokens.has(item.gallerySelectionToken)) {
            cancelVideoStatsProbe(item.videoStatsProbeId);
          }
        });
        setTimeout(() => setErrorMessage(null), 5000);
      }
    }

    setSlotsState(prev => prev.map(s => {
      if (s.slotId !== slotIdForMetadata) return s;
      const updatedItems = [...s.items];
      results.forEach(({ token, metadata }) => {
        const slotIndex = updatedItems.findIndex(item => item.gallerySelectionToken === token);
        if (slotIndex >= 0) {
          updatedItems[slotIndex] = {
            ...updatedItems[slotIndex],
            ...(metadata.dimensions ? { dimensions: metadata.dimensions } : {}),
            ...(metadata.duration !== undefined ? { duration: metadata.duration } : {})
          };
        }
      });
      return {
        ...s,
        items: updatedItems.filter(item =>
          !item.gallerySelectionToken || !rejectedTokens.has(item.gallerySelectionToken)
        ),
      };
    }));

    // --- Gallery image processing: crop/resize/JPEG conversion ---
    // needsProcessing was already computed above (before items were added to state).
    // Note: rejected items were already removed from state by the filter above,
    // so only surviving items (with processing: true) remain.
    if (needsProcessing) {
      // Collect indices of image items that survived rejection
      const survivingImageIndices = newItems
        .map((item, i) => ({ item, i }))
        .filter(({ item }) => item.mediaType === 'image')
        .map(({ i }) => i);

      if (survivingImageIndices.length > 0) {
        // processing: true was already set when items were first added to state

        // Process each gallery image asynchronously
        const processingNotifications: string[] = [];
        const processingPromises = survivingImageIndices.map(async (originalIndex) => {
          const item = newItems[originalIndex];
          if (!item || !item.url) return;

          const filename = item.url.split('/').pop()?.split('?')[0] || 'image.jpg';
          const itemGalleryId = item.galleryId;

          try {
            // Fetch gallery URL → blob → File
            const response = await fetch(item.url);
            if (!response.ok) throw new Error(`Fetch failed: ${response.status}`);
            const blob = await response.blob();
            const file = new File([blob], filename, { type: blob.type });

            const maxSizeBytes = (slotConfig.imageMaxFileSize || 20) * 1024 * 1024;

            // Step 1: Pre-process (downscale/compress) just like local uploads
            const processResult = await processImageForUpload(file, {
              maxWidth: slotConfig.imageMaxWidth ?? undefined,
              maxHeight: slotConfig.imageMaxHeight ?? undefined,
              maxFileSizeBytes: maxSizeBytes
            });

            let processedFile = processResult.file;
            let dims = processResult.finalDimensions || { width: 0, height: 0 };

            // Step 2: Crop/resize to target dimensions if configured
            // Save pre-crop state for manual crop adjustment
            const preCropFile = processedFile;
            const preCropDims = { ...dims };
            let cropResult: CropAndResizeResult | undefined;

            if (slotConfig.targetWidth && slotConfig.targetHeight) {
              const result = await cropAndResizeToTarget(processedFile, {
                targetWidth: slotConfig.targetWidth,
                targetHeight: slotConfig.targetHeight,
                convertToJpeg: slotConfig.convertToJpeg
              });
              cropResult = result;
              processedFile = result.file;
              dims = result.finalDimensions;
            } else if (slotConfig.convertToJpeg) {
              const result = await convertFileToJpeg(processedFile);
              cropResult = result;
              processedFile = result.file;
              dims = result.finalDimensions;
            }

            const galleryCropped = cropResult?.actions.includes('cropped') ?? false;

            // Check processed file against imageMaxFileSize limit
            if (processedFile.size > maxSizeBytes) {
              const originalUrl = item.url;
              let itemStillExists = false;
              flushSync(() => {
                setSlotsState(prev => prev.map(s => {
                  if (s.slotId !== slotIdForMetadata) return s;
                  return {
                    ...s,
                    items: s.items.filter((existing) => {
                      if (existing.processing && existing.url === originalUrl && existing.galleryId === itemGalleryId) {
                        itemStillExists = true;
                        return false;
                      }
                      return true;
                    })
                  };
                }));
              });
              if (itemStillExists) {
                setErrorMessage(`${filename}: ${t.widgets?.inputMedia?.fileTooLarge || 'File too large'} (max ${slotConfig.imageMaxFileSize || 20}MB)`);
                setTimeout(() => setErrorMessage(null), 5000);
              }
              return;
            }

            // Build notification for this item
            const parts: string[] = [];
            if (processResult.wasProcessed) {
              const actions = processResult.actions;
              if (actions.includes('resized') && actions.includes('compressed')) {
                parts.push(t.widgets?.inputMedia?.imageAutoResizedAndCompressed || 'Image resized and compressed');
              } else if (actions.includes('resized')) {
                parts.push(t.widgets?.inputMedia?.imageAutoResized || 'Image resized to fit allowed dimensions');
              } else if (actions.includes('compressed')) {
                parts.push(t.widgets?.inputMedia?.imageAutoCompressed || 'Image compressed to fit size limit');
              }
            }
            if (cropResult?.wasProcessed) {
              const cropActions = cropResult.actions;
              const hasCrop = cropActions.includes('cropped');
              const hasResize = cropActions.includes('resized');
              const hasJpeg = cropActions.includes('jpeg-converted');
              const dimStr = `(${dims.width}x${dims.height})`;
              if (hasCrop && hasJpeg) {
                parts.push(`${t.widgets?.inputMedia?.imageCroppedAndConverted || 'Cropped and converted to JPEG'} ${dimStr}`);
              } else if (hasResize && hasJpeg) {
                parts.push(`${t.widgets?.inputMedia?.imageResizedAndConverted || 'Resized and converted to JPEG'} ${dimStr}`);
              } else if (hasCrop) {
                parts.push(`${t.widgets?.inputMedia?.imageCroppedToTarget || 'Cropped to'} ${dimStr}`);
              } else if (hasResize) {
                parts.push(`${t.widgets?.inputMedia?.imageResizedToTarget || 'Resized to'} ${dimStr}`);
              } else if (hasJpeg) {
                parts.push(t.widgets?.inputMedia?.imageConvertedToJpeg || 'Converted to JPEG');
              }
            }
            const galleryItemOriginalUrl = item.url;
            const blobUrl = URL.createObjectURL(processedFile);
            mediaUrlMapRef.current.set(processedFile, blobUrl);
            const originalUrl_forCrop = galleryCropped ? URL.createObjectURL(preCropFile) : undefined;
            let itemStillExists = false;
            flushSync(() => {
              setSlotsState(prev => prev.map(s => {
                if (s.slotId !== slotIdForMetadata) return s;
                return {
                  ...s,
                  items: s.items.map((existing) => {
                    if (existing.processing && existing.url === galleryItemOriginalUrl && existing.galleryId === itemGalleryId) {
                      itemStillExists = true;
                      return {
                        ...existing,
                        url: blobUrl,
                        file: processedFile,
                        processing: false,
                        dimensions: dims.width > 0 ? dims : existing.dimensions,
                        ...(galleryCropped ? {
                          originalFile: preCropFile,
                          originalUrl: originalUrl_forCrop,
                          originalDimensions: preCropDims,
                          wasCropped: true
                        } : {})
                      };
                    }
                    return existing;
                  })
                };
              }));
            });
            if (!itemStillExists) {
              URL.revokeObjectURL(blobUrl);
              mediaUrlMapRef.current.delete(processedFile);
              if (originalUrl_forCrop) URL.revokeObjectURL(originalUrl_forCrop);
            } else if (parts.length > 0) {
              processingNotifications.push(`${filename}: ${parts.join(', ')}`);
            }
          } catch {
            const originalUrl = item.url;
            let itemStillExists = false;
            flushSync(() => {
              setSlotsState(prev => prev.map(s => {
                if (s.slotId !== slotIdForMetadata) return s;
                return {
                  ...s,
                  items: s.items.filter((existing) => {
                    if (existing.processing && existing.url === originalUrl && existing.galleryId === itemGalleryId) {
                      itemStillExists = true;
                      return false;
                    }
                    return true;
                  })
                };
              }));
            });
            if (itemStillExists) {
              const errorMsg = t.widgets?.slotsInputMedia?.errors?.processingFailed
                || 'Failed to process image';
              setErrorMessage(`${filename}: ${errorMsg}`);
              setTimeout(() => setErrorMessage(null), 5000);
            }
          }
        });

        await Promise.all(processingPromises);

        // Show processing notifications
        if (processingNotifications.length > 0) {
          showNotifications(processingNotifications);
        }
      }
    }
  }, [activeGallerySlotId, slotsState, getSlotConfig, getMediaMetadataFromUrl, t, showNotifications, cancelVideoStatsProbe, nextVideoStatsProbeId]);

  // IWorkflowWidget implementation via ref
  useImperativeHandle(widgetRef, () => ({
    getPayloadData(): WidgetPayloadResult {
      const requiredFiles: File[] = [];
      const galleryUrls: GalleryMediaItem[] = [];
      const inputImageDimensions: { width: number; height: number }[] = [];
      const data: Record<string, any> = {};

      let audioDuration = 0;
      let videoDuration = 0;
      const audioDurations: number[] = [];
      const videoDurations: number[] = [];
      let maxVideoFps = 0;
      let videoFrameCount = 0;
      let videoSizeBytes = 0;
      let imageCount = 0;
      let videoCount = 0;
      let audioCount = 0;
      let maxVideoWidth = 0;
      let maxVideoHeight = 0;
      let maxVideoMpx = 0;

      slotsState.forEach(slotState => {
        const slotConfig = getSlotConfig(slotState.slotId);
        const slotUrls: string[] = [];

        slotState.items.forEach(item => {
          // Collect URL for this slot
          slotUrls.push(item.url);

          if (item.file) {
            // Processed gallery items (with file) go to requiredFiles, not galleryUrls
            requiredFiles.push(item.file);
          } else if (item.source === 'gallery' && item.galleryId) {
            galleryUrls.push({
              id: item.galleryId,
              url: item.url,
              previewUrl: item.url,
              mediaType: item.mediaType
            });
          }

          if (item.mediaType === 'image') {
            imageCount++;
            if (item.dimensions) {
              inputImageDimensions.push(item.dimensions);
            }
          } else if (item.mediaType === 'video') {
            videoCount++;
            if (item.duration) {
              videoDuration += Math.max(1, Math.round(item.duration));
              videoDurations.push(item.duration);
            }
            if (item.fps && Number.isFinite(item.fps)) {
              maxVideoFps = Math.max(maxVideoFps, item.fps);
            }
            if (item.frameCount && Number.isFinite(item.frameCount)) {
              videoFrameCount += item.frameCount;
            }
            if (item.sizeBytes && Number.isFinite(item.sizeBytes)) {
              videoSizeBytes += item.sizeBytes;
            }
            // Track max video dimensions for pricing formulas (same as BigInputMediaWidget)
            if (item.dimensions) {
              const mpx = (item.dimensions.width * item.dimensions.height) / 1_000_000;
              if (mpx > maxVideoMpx) {
                maxVideoWidth = item.dimensions.width;
                maxVideoHeight = item.dimensions.height;
                maxVideoMpx = mpx;
              }
            }
          } else if (item.mediaType === 'audio') {
            audioCount++;
            if (item.duration) {
              audioDuration += Math.max(1, Math.round(item.duration));
              audioDurations.push(item.duration);
            }
          }
        });

        // Add data under slot_id key if slot has items
        if (slotConfig?.slot_id && slotUrls.length > 0) {
          if (slotConfig.maxCount === 1) {
            // Single value - string
            data[slotConfig.slot_id] = slotUrls[0];
          } else {
            // Multiple values - array
            data[slotConfig.slot_id] = slotUrls;
          }
        }
      });

      // Add counters
      if (audioDuration > 0) {
        data.audio_duration = audioDuration;
        data.audio_duration_exact = sumExactMediaDurations(audioDurations);
      }
      if (videoDuration > 0) {
        data.video_duration = videoDuration;
        data.video_duration_exact = sumExactMediaDurations(videoDurations);
      }
      if (maxVideoFps > 0) data.video_fps = maxVideoFps;
      if (videoFrameCount > 0) data.video_frame_count = videoFrameCount;
      if (videoSizeBytes > 0) data.video_size_bytes = videoSizeBytes;
      if (imageCount > 0) data.slots_image_count = imageCount;
      if (videoCount > 0) data.slots_video_count = videoCount;
      if (audioCount > 0) data.slots_audio_count = audioCount;
      if (maxVideoWidth > 0) data.video_width = maxVideoWidth;
      if (maxVideoHeight > 0) data.video_height = maxVideoHeight;
      if (maxVideoMpx > 0) data.video_mpx = Math.round(maxVideoMpx * 1000) / 1000;

      return {
        data,
        requiredFiles: requiredFiles.length > 0 ? requiredFiles : undefined,
        galleryUrls: galleryUrls.length > 0 ? galleryUrls : undefined,
        inputImageDimensions: inputImageDimensions.length > 0 ? inputImageDimensions : undefined,
        inputImageDimensionsSource: 'slots'
      };
    },

    validateState(): WidgetValidationError[] {
      const errors: WidgetValidationError[] = unsupportedModelSlots
        .filter(slot => slot.required)
        .map(slot => ({
          field: slot.slot_id,
          message: `${getSlotName(slot)}: ${t.widgets?.slotsInputMedia?.modelUnsupportedInSmall
            || '3D models are supported only by the large slots media widget.'}`,
        }));

      config?.slots?.forEach(slotConfig => {
        const slotState = getSlotState(slotConfig.slot_id);
        const itemCount = slotState?.items.length || 0;

        if (slotConfig.required && itemCount === 0) {
          const slotName = getSlotName(slotConfig);
          errors.push({
            field: slotConfig.slot_id,
            message: `${slotName}: ${t.widgets?.slotsInputMedia?.required || 'Required'}`
          });
        }

        // Block submission while gallery images are still being processed
        const hasProcessing = slotState?.items.some(item => item.processing);
        if (hasProcessing) {
          const slotName = getSlotName(slotConfig);
          errors.push({
            field: slotConfig.slot_id,
            message: `${slotName}: ${t.widgets?.slotsInputMedia?.errors?.stillProcessing || 'Images are still being processed'}`
          });
        }

        // Revalidate staged videos at submission time. Interactive uploads and gallery
        // picks are checked before insertion, but recreate/sessionStorage items are restored
        // first and measured asynchronously, so this is the final guard for every path.
        if (slotConfig.mediaType === 'video') {
          const constraintError = slotState?.items.reduce<string | null>((error, item) => (
            error || validateVideoSlotConstraints({
              width: item.dimensions?.width,
              height: item.dimensions?.height,
              duration: item.duration,
            }, slotConfig, t.widgets?.slotsInputMedia?.errors)
          ), null);
          if (constraintError) {
            errors.push({
              field: slotConfig.slot_id,
              message: `${getSlotName(slotConfig)}: ${constraintError}`,
            });
          }
        }
      });

      getUnmeasuredMediaPricingKeys(slotsState.flatMap(slot => slot.items)).forEach(field => {
        errors.push({
          field,
          message: getInvalidPricingAggregateMessage(language),
        });
      });

      return errors;
    },

    isRequired(): boolean {
      return config?.slots?.some(s => s.required)
        || unsupportedModelSlots.some(s => s.required)
        || false;
    }
  }), [slotsState, config, unsupportedModelSlots, getSlotConfig, getSlotState, getSlotName, t, language]);

  // Get media icon
  const getMediaIcon = (type: MediaType) => {
    switch (type) {
      case 'image': return ICONS.IMAGE;
      case 'video': return ICONS.VIDEO;
      case 'audio': return ICONS.AUDIO;
    }
  };

  // Get media type accept object (uses specific MIME types when formats are restricted)
  const getAcceptForType = (type: MediaType, acceptedFormats?: string[]) => {
    const formats = acceptedFormats?.length ? acceptedFormats : DEFAULT_FORMATS[type];
    if (acceptedFormats?.length) {
      const accept: Record<string, string[]> = {};
      for (const ext of formats) {
        const mime = EXT_TO_MIME[ext];
        if (mime) {
          if (!accept[mime]) accept[mime] = [];
          accept[mime].push(ext);
        }
      }
      return accept;
    }
    switch (type) {
      case 'image': return { 'image/*': formats };
      case 'video': return { 'video/*': formats };
      case 'audio': return { 'audio/*': formats };
    }
  };

  // Open crop adjustment modal for a cropped item
  const handleCropClick = useCallback((slotId: string, itemIndex: number) => {
    const slotState = getSlotState(slotId);
    const item = slotState?.items[itemIndex];
    const slotCfg = getSlotConfig(slotId);
    if (!item?.originalUrl || !item?.originalDimensions || !slotCfg?.targetWidth || !slotCfg?.targetHeight) return;
    setCropModalSlotId(slotId);
    setCropModalItemIndex(itemIndex);
  }, [getSlotState, getSlotConfig]);

  // Apply crop result — update slot item with new cropped file/url
  const handleCropApply = useCallback((croppedFile: File, blobUrl: string, dimensions: { width: number; height: number }) => {
    if (!cropModalSlotId || cropModalItemIndex === null) return;

    setSlotsState(prev => prev.map(slot => {
      if (slot.slotId !== cropModalSlotId) return slot;
      return {
        ...slot,
        items: slot.items.map((item, idx) => {
          if (idx !== cropModalItemIndex) return item;
          // Revoke old cropped blob URL via mediaUrlMap
          if (item.file) {
            const oldMapUrl = mediaUrlMapRef.current.get(item.file);
            if (oldMapUrl) {
              URL.revokeObjectURL(oldMapUrl);
              mediaUrlMapRef.current.delete(item.file);
            }
          }
          // Track new file in mediaUrlMap
          mediaUrlMapRef.current.set(croppedFile, blobUrl);
          return {
            ...item,
            file: croppedFile,
            url: blobUrl,
            dimensions,
          };
        }),
      };
    }));

    setCropModalSlotId(null);
    setCropModalItemIndex(null);
  }, [cropModalSlotId, cropModalItemIndex]);

  // Close crop modal without changes
  const handleCropCancel = useCallback(() => {
    setCropModalSlotId(null);
    setCropModalItemIndex(null);
  }, []);

  // Render compact slot
  const renderSlot = (slotConfig: SlotConfig) => {
    const slotState = getSlotState(slotConfig.slot_id);
    const items = slotState?.items || [];
    const canAddMore = items.length < slotConfig.maxCount;
    const slotName = getSlotName(slotConfig);

    return (
      <CompactSlot
        key={slotConfig.slot_id}
        slotConfig={slotConfig}
        slotName={slotName}
        items={items}
        canAddMore={canAddMore}
        language={language}
        onFilesAdded={(files) => addMediaToSlot(slotConfig.slot_id, files)}
        onRemove={(index) => removeMediaFromSlot(slotConfig.slot_id, index)}
        onGalleryClick={() => setActiveGallerySlotId(slotConfig.slot_id)}
        onCropClick={(index) => handleCropClick(slotConfig.slot_id, index)}
        getMediaIcon={getMediaIcon}
        getAcceptForType={getAcceptForType}
        onDropRejected={(fileRejections) => {
          if (fileRejections.length === 0) return;
          const rejected = fileRejections[0];
          const fileName = rejected.file.name;
          const formats = slotConfig.acceptedFormats?.length
            ? slotConfig.acceptedFormats.join(', ')
            : DEFAULT_FORMATS[slotConfig.mediaType].join(', ');
          const msg = `${t.widgets?.slotsInputMedia?.fileRejected || 'File rejected'}: "${fileName}". ${t.widgets?.slotsInputMedia?.allowedFormats || 'Allowed formats'}: ${formats}`;
          setErrorMessage(msg);
          setTimeout(() => setErrorMessage(null), 3000);
        }}
      />
    );
  };

  if ((!config?.slots || config.slots.length === 0) && unsupportedModelSlots.length === 0) {
    return (
      <BaseWidget
        title={t.widgets?.slotsInputMedia?.title || 'Slots Input Media'}
        icon={<Icon name={ICONS.UPLOAD} size={16} className="text-current" />}
        description={t.widgets?.slotsInputMedia?.description || 'Upload media to slots'}
        language={language}
        className={className}
        widgetId="slots_input_media_small"
      >
        <div className="text-center py-4 text-gray-500 text-xs">
          <Icon name={ICONS.INFO} size={20} className="mx-auto mb-1" />
          <p>{t.widgets?.slotsInputMedia?.noSlotsConfigured || 'No slots configured'}</p>
        </div>
      </BaseWidget>
    );
  }

  return (
    <BaseWidget
      title={t.widgets?.slotsInputMedia?.title || 'Slots Input Media'}
      icon={<Icon name={ICONS.UPLOAD} size={16} className="text-current" />}
      description={t.widgets?.slotsInputMedia?.description || 'Upload media to slots'}
      language={language}
      className={className}
      widgetId="slots_input_media_small"
    >
      {unsupportedModelSlots.length > 0 && (
        <div className="mb-2 rounded border border-amber-700 bg-amber-950/40 p-2 text-xs text-amber-300">
          {t.widgets?.slotsInputMedia?.modelUnsupportedInSmall
            || '3D models are supported only by the large slots media widget.'}
        </div>
      )}
      {/* Auto-processing notification banner (compact) */}
      {processedNotifications.length > 0 && (
        <div className="mb-2 bg-blue-900/90 border border-blue-600 rounded p-2 backdrop-blur-sm">
          <div className="flex items-start gap-1.5">
            <Icon name={ICONS.INFO} size={14} className="text-blue-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-blue-200 font-medium text-[11px]">
                {t.widgets?.inputMedia?.imageAutoProcessedInfo || 'Automatically processed'}
              </p>
              <ul className="text-blue-300 text-[10px] mt-0.5 space-y-0.5">
                {processedNotifications.map((msg, i) => (
                  <li key={i} className="truncate">{msg}</li>
                ))}
              </ul>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setProcessedNotifications([]);
              }}
              className="text-blue-400 hover:text-blue-200 transition-colors flex-shrink-0"
            >
              <Icon name={ICONS.X} size={14} />
            </button>
          </div>
        </div>
      )}

      {/* Error message */}
      {errorMessage && (
        <div className="mb-2 px-2 py-1 bg-red-500/20 border border-red-500/50 rounded text-red-400 text-xs">
          {errorMessage}
        </div>
      )}

      {/* Slots list - vertical layout for sidebar */}
      <div className="space-y-2">
        {config.slots.map(renderSlot)}
      </div>

      {/* Gallery selection modal */}
      {activeGallerySlotId && (
        <GallerySelectionModal
          isOpen={true}
          onClose={() => setActiveGallerySlotId(null)}
          onSelect={handleGallerySelect}
          language={language}
          maxSelection={getSlotConfig(activeGallerySlotId)?.maxCount || 1}
          allowedMediaTypes={
            getSlotConfig(activeGallerySlotId)?.mediaType
              ? [getSlotConfig(activeGallerySlotId)!.mediaType]
              : undefined
          }
          initialSelectedIds={
            (() => {
              const slotState = getSlotState(activeGallerySlotId);
              return slotState
                ? slotState.items
                    .filter(item => item.source === 'gallery' && item.galleryId && item.galleryId !== -1)
                    .map(item => item.galleryId!)
                : [];
            })()
          }
          mediaConstraints={getSlotConfig(activeGallerySlotId)}
        />
      )}

      {/* Crop adjustment modal */}
      {cropModalSlotId && cropModalItemIndex !== null && (() => {
        const slotState = getSlotState(cropModalSlotId);
        const item = slotState?.items[cropModalItemIndex];
        const slotCfg = getSlotConfig(cropModalSlotId);
        if (!item?.originalUrl || !item?.originalDimensions || !slotCfg?.targetWidth || !slotCfg?.targetHeight) return null;
        return (
          <CropAdjustmentModal
            isOpen={true}
            originalUrl={item.originalUrl}
            targetWidth={slotCfg.targetWidth}
            targetHeight={slotCfg.targetHeight}
            convertToJpeg={slotCfg.convertToJpeg}
            onApply={handleCropApply}
            onCancel={handleCropCancel}
            language={language}
          />
        );
      })()}
    </BaseWidget>
  );
}

// Compact slot component for sidebar
interface CompactSlotProps {
  slotConfig: SlotConfig;
  slotName: string;
  items: SlotMediaItem[];
  canAddMore: boolean;
  language: Language;
  onFilesAdded: (files: File[]) => void;
  onRemove: (index: number) => void;
  onGalleryClick: () => void;
  onCropClick: (index: number) => void;
  getMediaIcon: (type: MediaType) => string;
  getAcceptForType: (type: MediaType, acceptedFormats?: string[]) => Record<string, string[]>;
  onDropRejected: (fileRejections: FileRejection[]) => void;
}

function CompactSlot({
  slotConfig,
  slotName,
  items,
  canAddMore,
  language,
  onFilesAdded,
  onRemove,
  onGalleryClick,
  onCropClick,
  getMediaIcon,
  getAcceptForType,
  onDropRejected
}: CompactSlotProps) {
  const t = useLocalization(language);

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop: onFilesAdded,
    onDropRejected,
    accept: getAcceptForType(slotConfig.mediaType, slotConfig.acceptedFormats),
    multiple: slotConfig.maxCount > 1,
    noClick: items.length > 0,
    disabled: slotConfig.maxCount > 1 ? !canAddMore : false
  });

  return (
    <div className="bg-gray-800/30 rounded-lg p-2">
      {/* Slot header */}
      <div className="flex items-center gap-1.5 mb-1.5">
        <Icon
          name={getMediaIcon(slotConfig.mediaType)}
          size={12}
          className={
            slotConfig.mediaType === 'image' ? 'text-blue-400' :
            slotConfig.mediaType === 'video' ? 'text-purple-400' :
            'text-orange-400'
          }
        />
        <span className="text-xs font-medium text-white flex-1 truncate">{slotName}</span>
        {slotConfig.required && <span className="text-red-400 text-[10px]">*</span>}
        <span className={`text-[10px] ${items.length >= slotConfig.maxCount ? 'text-green-400' : 'text-gray-500'}`}>{items.length}/{slotConfig.maxCount}</span>
      </div>

      {/* Drop zone / media display */}
      <div
        {...getRootProps()}
        className={`
          border border-dashed rounded p-1.5 transition-all
          ${isDragActive ? 'border-purple-400 bg-purple-600/10' : 'border-gray-700 hover:border-gray-600'}
          ${slotConfig.maxCount > 1 && !canAddMore && items.length > 0 ? 'border-solid border-gray-700' : ''}
        `}
      >
        <input {...getInputProps()} />

        {items.length === 0 ? (
          // Empty state
          <div className="flex flex-col items-center justify-center text-center py-2">
            <Icon name={getMediaIcon(slotConfig.mediaType)} size={24} className="text-gray-500 mb-1" />
            <p className="text-[10px] text-gray-400 mb-1">{items.length}/{slotConfig.maxCount}</p>
            <p className="text-xs text-gray-400 mb-2">
              {isDragActive
                ? (t.widgets?.inputMedia?.dropHere || 'Drop here')
                : (t.widgets?.slotsInputMedia?.dropOrClick || 'Drop or click')
              }
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  open();
                }}
                className="px-3 py-1.5 text-xs bg-purple-600/30 hover:bg-purple-600/50 text-purple-300 rounded transition-colors flex items-center gap-1"
              >
                <Icon name={ICONS.UPLOAD} size={14} />
                {t.widgets?.slotsInputMedia?.upload || 'Upload'}
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onGalleryClick();
                }}
                className="px-3 py-1.5 text-xs bg-blue-600/30 hover:bg-blue-600/50 text-blue-300 rounded transition-colors flex items-center gap-1"
              >
                <Icon name={ICONS.GALLERY} size={14} />
                {t.widgets?.slotsInputMedia?.gallery || 'Gallery'}
              </button>
            </div>
            <div className="mt-1 text-[10px] text-gray-500 flex flex-wrap gap-x-2 justify-center">
              {slotConfig.mediaType === 'image' && (
                <>
                  {slotConfig.imageMaxFileSize && <span>max {slotConfig.imageMaxFileSize}MB</span>}
                  {(slotConfig.imageMinWidth || slotConfig.imageMaxWidth) && (
                    <span>W: {slotConfig.imageMinWidth || '?'}-{slotConfig.imageMaxWidth || '?'}px</span>
                  )}
                  {(slotConfig.imageMinHeight || slotConfig.imageMaxHeight) && (
                    <span>H: {slotConfig.imageMinHeight || '?'}-{slotConfig.imageMaxHeight || '?'}px</span>
                  )}
                </>
              )}
              {slotConfig.mediaType === 'video' && (
                <>
                  {describeVideoSlotConstraints(slotConfig).map(description => (
                    <span key={description}>{description}</span>
                  ))}
                </>
              )}
              {slotConfig.mediaType === 'audio' && (
                <>
                  {slotConfig.audioMaxFileSize && <span>max {slotConfig.audioMaxFileSize}MB</span>}
                  {slotConfig.audioMaxDuration && <span>max {slotConfig.audioMaxDuration}s</span>}
                </>
              )}
            </div>
          </div>
        ) : (
          // Media thumbnails - horizontal row
          <div className="flex gap-1.5 flex-wrap">
            {items.map((item, index) => (
              <div
                key={index}
                className={`relative group w-32 h-32 flex-shrink-0 bg-gray-800 rounded-lg overflow-hidden border-2 border-gray-600 hover:border-gray-500 transition-colors${item.wasCropped ? ' cursor-pointer' : ''}`}
                onClick={() => { if (item.wasCropped) onCropClick(index); }}
              >
                {item.mediaType === 'image' && (
                  <img src={item.url} alt="" className="w-full h-full object-cover" />
                )}
                {item.mediaType === 'video' && (
                  <div className="w-full h-full relative">
                    <video src={item.url} className="w-full h-full object-cover" preload="metadata" muted />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                      <Icon name={ICONS.PLAY} size={24} className="text-white" />
                    </div>
                  </div>
                )}
                {item.mediaType === 'audio' && (
                  <div className="w-full h-full bg-orange-900/30 flex flex-col items-center justify-center">
                    <Icon name={ICONS.AUDIO} size={28} className="text-orange-400" />
                    {item.duration && (
                      <span className="text-xs text-gray-400 mt-1">{formatDuration(item.duration)}</span>
                    )}
                  </div>
                )}

                {/* Processing overlay */}
                {item.processing && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-10">
                    <svg className="animate-spin h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                  </div>
                )}

                {/* Remove button — disabled while processing to prevent stale async callback */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!item.processing) onRemove(index);
                  }}
                  disabled={item.processing}
                  className={`absolute top-1 right-1 w-6 h-6 rounded-full flex items-center justify-center text-white transition-opacity ${
                    item.processing
                      ? 'bg-gray-500/50 cursor-not-allowed opacity-0'
                      : 'bg-red-500/80 hover:bg-red-500 opacity-0 group-hover:opacity-100'
                  }`}
                >
                  <Icon name={ICONS.X} size={14} />
                </button>

                {/* Gallery badge */}
                {item.source === 'gallery' && (
                  <div className="absolute bottom-1 left-1 px-1.5 py-0.5 bg-blue-500/80 rounded text-[10px] text-white">
                    {t.widgets?.slotsInputMedia?.gallery || 'Gallery'}
                  </div>
                )}

                {/* Cropped badge */}
                {item.wasCropped && (
                  <div
                    className="absolute bottom-1 right-1 p-1 bg-amber-500/80 rounded flex items-center justify-center"
                    title={t.widgets?.slotsInputMedia?.cropped || 'Cropped — click to adjust'}
                  >
                    <Icon name={ICONS.CROP} size={12} className="text-white" />
                  </div>
                )}
              </div>
            ))}

            {/* Add more button */}
            {canAddMore && (
              <div className="w-32 h-32 flex-shrink-0 border-2 border-dashed border-gray-700 rounded-lg flex flex-col items-center justify-center gap-2 hover:border-gray-600 transition-colors">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    open();
                  }}
                  className="px-3 py-1.5 text-xs bg-purple-600/30 hover:bg-purple-600/50 text-purple-300 rounded transition-colors flex items-center gap-1"
                >
                  <Icon name={ICONS.UPLOAD} size={14} />
                  {t.widgets?.slotsInputMedia?.upload || 'Upload'}
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onGalleryClick();
                  }}
                  className="px-3 py-1.5 text-xs bg-blue-600/30 hover:bg-blue-600/50 text-blue-300 rounded transition-colors flex items-center gap-1"
                >
                  <Icon name={ICONS.GALLERY} size={14} />
                  {t.widgets?.slotsInputMedia?.gallery || 'Gallery'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

    </div>
  );
}
